import CustomerProfileFields from "./CustomerProfileFields";
import { customerProfilePayload } from "../shared/customerProfile";
import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  Users,
  UserPlus,
  Star,
  Calendar,
  Download,
  PlusCircle,
  Phone,
  Mail,
  Gift,
  RefreshCw,
  Search,
  Edit,
  Trash2,
  TrendingUp,
  Loader2,
  Award,
  History,
  Filter,
  ShieldCheck,
  User,
} from "lucide-react";

const API_BASE = "/api";
const LOYAL_THRESHOLD = 10;

const filterOptions = [
  { value: "all", label: "Tous" },
  { value: "new", label: "Nouveaux" },
  { value: "loyal", label: "Fidèles" },
  { value: "favorite", label: "Favoris" },
];

const sortOptions = [
  { value: "recent", label: "Dernière visite" },
  { value: "orders", label: "Nombre de commandes" },
  { value: "spent", label: "Montant total" },
  { value: "name", label: "Nom" },
];

const topPeriodOptions = [
  { value: "week", label: "Cette semaine" },
  { value: "month", label: "Ce mois" },
  { value: "year", label: "Cette année" },
];

const formatCurrency = (value = 0) =>
  `${new Intl.NumberFormat("fr-FR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number(value) || 0)} FCFA`;

const formatNumber = (value = 0) =>
  new Intl.NumberFormat("fr-FR", {
    maximumFractionDigits: 0,
  }).format(Number(value) || 0);

const formatDate = (value) => {
  if (!value) return "—";
  return new Date(value).toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatDateTime = (value) => {
  if (!value) return "—";
  return new Date(value).toLocaleString("fr-FR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const isSameMonth = (dateString) => {
  if (!dateString) return false;
  const target = new Date(dateString);
  const now = new Date();
  return (
    target.getMonth() === now.getMonth() &&
    target.getFullYear() === now.getFullYear()
  );
};

const formatOrderLabel = (value) => {
  if (!value) return "—";
  return value.startsWith("#") ? value : `#${value}`;
};

const escapeXlsValue = (value) => {
  if (value == null) return "";
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
};

const CustomerManagement = () => {
  const [customers, setCustomers] = useState([]);
  const [stats, setStats] = useState({
    totalCustomers: 0,
    newCustomersThisMonth: 0,
    loyalCustomers: 0,
    birthdaysThisMonth: [],
    topCustomers: [],
  });
  const [topCustomers, setTopCustomers] = useState([]);
  const [topPeriod, setTopPeriod] = useState("month");
  const [topLimit, setTopLimit] = useState(5);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  const [sortBy, setSortBy] = useState("recent");
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerOrders, setCustomerOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersError, setOrdersError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    civility: "Mr",
    first_name: "",
    last_name: "",
    phone: "",
    email: "",
    birth_date: "",
    notes: "",
    is_favorite: false,
  });
  const [isDarkMode, setIsDarkMode] = useState(() =>
    typeof document !== "undefined"
      ? document.documentElement.classList.contains("dark")
      : false,
  );
  const ordersCacheRef = useRef({});

  useEffect(() => {
    const target = document.documentElement;
    const syncTheme = () =>
      setIsDarkMode(target.classList.contains("dark"));
    syncTheme();
    const observer = new MutationObserver(syncTheme);
    observer.observe(target, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);

  const fetchCustomers = useCallback(
    async (term = "") => {
      try {
        setLoading(true);
        setError(null);

        const params = new URLSearchParams();
        if (term) {
          params.set("search", term);
        }
        const query = params.toString();
        const response = await fetch(
          `${API_BASE}/customers${query ? `?${query}` : ""}`,
        );
        if (!response.ok) {
          throw new Error("Erreur lors du chargement des clients");
        }
        const data = await response.json();
        setCustomers(data);
        setSelectedCustomer((prev) =>
          prev ? data.find((c) => c.id === prev.id) || prev : null,
        );
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    },
    [setCustomers],
  );

  const fetchStats = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/customers/stats/overview`);
      if (!response.ok) {
        throw new Error("Impossible de charger les statistiques");
      }
      const data = await response.json();
      setStats(data);
    } catch (err) {
      console.error(err);
    }
  }, []);

  const fetchTopCustomers = useCallback(async (period, limitValue) => {
    try {
      const response = await fetch(
        `${API_BASE}/customers/top?period=${period}&limit=${limitValue}`,
      );
      if (!response.ok) {
        throw new Error("Impossible de charger les meilleurs clients");
      }
      const data = await response.json();
      setTopCustomers(data.customers || []);
    } catch (err) {
      console.error(err);
    }
  }, []);

  const loadCustomerOrders = useCallback(async (customer) => {
    if (!customer) {
      setCustomerOrders([]);
      return;
    }

    const cached = ordersCacheRef.current[customer.id];
    if (cached) {
      setCustomerOrders(cached);
      return;
    }

    try {
      setOrdersLoading(true);
      setOrdersError(null);
      const response = await fetch(
        `${API_BASE}/customers/${customer.id}/orders?limit=20`,
      );
      if (!response.ok) {
        throw new Error("Impossible de charger l'historique");
      }
      const data = await response.json();
      ordersCacheRef.current[customer.id] = data.orders || [];
      setCustomerOrders(data.orders || []);
    } catch (err) {
      setOrdersError(err.message);
      setCustomerOrders([]);
    } finally {
      setOrdersLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCustomers();
    fetchStats();
    fetchTopCustomers(topPeriod, topLimit);
  }, [fetchCustomers, fetchStats, fetchTopCustomers, topPeriod, topLimit]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchCustomers(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, fetchCustomers]);

  useEffect(() => {
    loadCustomerOrders(selectedCustomer);
  }, [selectedCustomer, loadCustomerOrders]);

  const containerBg = isDarkMode
    ? "bg-gradient-to-br from-[#040a18] via-[#0c182f] to-[#040a18] text-slate-100"
    : "bg-slate-50 text-slate-900";
  const cardClasses = isDarkMode
    ? "bg-white/5 border border-white/10 shadow-2xl shadow-black/40"
    : "bg-white border border-slate-200 shadow-lg shadow-slate-200/60";
  const subtleText = isDarkMode ? "text-slate-400" : "text-slate-500";
  const handleTopLimitChange = (value) => {
    const numeric = Math.max(1, Math.min(50, Number(value) || 1));
    setTopLimit(numeric);
  };

  const resetForm = useCallback(() => {
    setFormData({
      civility: "Mr",
      first_name: "",
      last_name: "",
      phone: "",
      email: "",
      birth_date: "",
      notes: "",
      is_favorite: false,
    });
    setShowForm(false);
  }, []);

  const isCustomerLoyal = useCallback(
    (customer) =>
      (customer?.total_orders || 0) >= LOYAL_THRESHOLD ||
      Boolean(customer?.is_favorite),
    [],
  );

  const filteredCustomers = useMemo(() => {
    let base = [...customers];
    if (activeFilter === "new") {
      base = base.filter((customer) => isSameMonth(customer.created_at));
    } else if (activeFilter === "loyal") {
      base = base.filter(isCustomerLoyal);
    } else if (activeFilter === "favorite") {
      base = base.filter((customer) => customer.is_favorite);
    }

    base.sort((a, b) => {
      if (sortBy === "orders") {
        return (b.total_orders || 0) - (a.total_orders || 0);
      }
      if (sortBy === "spent") {
        return (b.total_spent || 0) - (a.total_spent || 0);
      }
      if (sortBy === "name") {
        return `${a.last_name}${a.first_name}`.localeCompare(
          `${b.last_name}${b.first_name}`,
        );
      }
      const dateA = new Date(
        a.last_visit_date || a.created_at || 0,
      ).getTime();
      const dateB = new Date(
        b.last_visit_date || b.created_at || 0,
      ).getTime();
      return dateB - dateA;
    });

    return base;
  }, [customers, activeFilter, sortBy, isCustomerLoyal]);

  const averageBasket = (customer) => {
    if (!customer || !customer.total_orders) return 0;
    return (customer.total_spent || 0) / customer.total_orders;
  };

  const handleSelectCustomer = (customer) => {
    setSelectedCustomer(customer);
    setSuccess(null);
    setError(null);
  };

  const handleNewCustomer = () => {
    resetForm();
    setShowForm(true);
  };

  const handleEditCustomer = (customer) => {
    setFormData({
      civility: customer.civility,
      first_name: customer.first_name,
      last_name: customer.last_name,
      phone: customer.phone,
      email: customer.email || "",
      birth_date: customer.birth_date || "",
      notes: customer.notes || "",
      is_favorite: Boolean(customer.is_favorite),
    });
    setSelectedCustomer(customer);
    setShowForm(true);
  };

  const handleDeleteCustomer = async (customerId) => {
    if (
      !window.confirm(
        "Êtes-vous sûr de vouloir supprimer ce client du répertoire ?",
      )
    ) {
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${API_BASE}/customers/${customerId}`, {
        method: "DELETE",
      });
      if (!response.ok) {
        throw new Error("Suppression impossible");
      }
      setSuccess("Client supprimé");
      if (selectedCustomer?.id === customerId) {
        setSelectedCustomer(null);
      }
      ordersCacheRef.current = {};
      await fetchCustomers(searchTerm);
      await fetchStats();
      await fetchTopCustomers(topPeriod, topLimit);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFavoriteToggle = async (customer) => {
    const nextValue = !customer.is_favorite;
    try {
      const response = await fetch(
        `${API_BASE}/customers/${customer.id}/favorite`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ is_favorite: nextValue }),
        },
      );
      if (!response.ok) {
        throw new Error("Impossible de mettre à jour le statut favori");
      }
      ordersCacheRef.current = {};
      await fetchCustomers(searchTerm);
      await fetchStats();
      await fetchTopCustomers(topPeriod, topLimit);
      setSuccess(
        nextValue
          ? "Client ajouté aux favoris"
          : "Client retiré des favoris",
      );
    } catch (err) {
      setError(err.message);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setIsSubmitting(true);
      setError(null);
      setSuccess(null);
      const url = selectedCustomer
        ? `${API_BASE}/customers/${selectedCustomer.id}`
        : `${API_BASE}/customers`;
      const method = selectedCustomer ? "PUT" : "POST";
      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(customerProfilePayload(formData)),
      });
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.error || "Échec de l'enregistrement");
      }
      setSuccess(
        selectedCustomer
          ? "Client mis à jour avec succès"
          : "Client ajouté avec succès",
      );
      setShowForm(false);
      ordersCacheRef.current = {};
      await fetchCustomers(searchTerm);
      await fetchStats();
      await fetchTopCustomers(topPeriod, topLimit);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const exportCustomers = () => {
    const columns = [
      { label: "Civilité", accessor: (c) => c.civility },
      { label: "Prénom", accessor: (c) => c.first_name },
      { label: "Nom", accessor: (c) => c.last_name },
      {
        label: "Téléphone",
        accessor: (c) => c.phone,
        cellAttrs: 'style="mso-number-format:\\@;"',
        prefix: "",
      },
      { label: "Email", accessor: (c) => c.email || "" },
      { label: "Date de naissance", accessor: (c) => c.birth_date || "" },
      { label: "Commandes", accessor: (c) => c.total_orders },
      { label: "Total dépensé", accessor: (c) => c.total_spent },
      {
        label: "Panier moyen",
        accessor: (c) =>
          c.total_orders && c.total_orders > 0
            ? (c.total_spent / c.total_orders).toFixed(2)
            : "0",
      },
      { label: "Favori", accessor: (c) => (isCustomerLoyal(c) ? "Oui" : "Non") },
      { label: "Date création", accessor: (c) => c.created_at },
    ];

    const tableRows = filteredCustomers
      .map((customer) => {
        const cells = columns
          .map((column) => {
            const rawValue = column.accessor(customer);
            const escapedValue = escapeXlsValue(
              column.prefix ? `${column.prefix}${rawValue}` : rawValue,
            );
            const attrs = column.cellAttrs ? ` ${column.cellAttrs}` : "";
            return `<td${attrs}>${escapedValue}</td>`;
          })
          .join("");
        return `<tr>${cells}</tr>`;
      })
      .join("");

    const headerRow = `<tr>${columns
      .map((column) => `<th>${escapeXlsValue(column.label)}</th>`)
      .join("")}</tr>`;

    const htmlTable = `
      <html xmlns:o="urn:schemas-microsoft-com:office:office"
            xmlns:x="urn:schemas-microsoft-com:office:excel"
            xmlns="http://www.w3.org/TR/REC-html40">
      <head><!--[if gte mso 9]>
      <xml>
        <x:ExcelWorkbook>
          <x:ExcelWorksheets>
            <x:ExcelWorksheet>
              <x:Name>Clients</x:Name>
              <x:WorksheetOptions>
                <x:DisplayGridlines/>
              </x:WorksheetOptions>
            </x:ExcelWorksheet>
          </x:ExcelWorksheets>
        </x:ExcelWorkbook>
      </xml>
      <![endif]--></head>
      <body>
        <table border="1">
          <thead>${headerRow}</thead>
          <tbody>${tableRows}</tbody>
        </table>
      </body>
      </html>
    `;

    const blob = new Blob([htmlTable], {
      type: "application/vnd.ms-excel",
    });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `clients_${new Date().toISOString().split("T")[0]}.xls`;
    link.click();
  };

  return (
    <div className={`min-h-screen px-4 py-6 ${containerBg}`}>
      <CustomerFormModal
        isOpen={showForm}
        onClose={resetForm}
        onSubmit={handleSubmit}
        formData={formData}
        setFormData={setFormData}
        isEditing={Boolean(selectedCustomer)}
        isSubmitting={isSubmitting}
        isDarkMode={isDarkMode}
      />

      <div className="max-w-7xl mx-auto space-y-6">
        <header className={`${cardClasses} rounded-3xl p-6 flex flex-col gap-4`}>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <p className={`text-xs uppercase tracking-[0.4em] ${subtleText}`}>
                Admin · Répertoire client
              </p>
              <h1 className="text-3xl font-semibold flex items-center gap-2">
                <Users className="text-amber-400" />
                Clients enregistrés
              </h1>
            </div>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={exportCustomers}
                className="inline-flex items-center gap-2 rounded-2xl border border-white/10 px-4 py-2 text-sm font-semibold hover:border-amber-400/60"
              >
                <Download size={18} />
                Export CSV
              </button>
              <button
                onClick={handleNewCustomer}
                className="inline-flex items-center gap-2 rounded-2xl bg-gradient-to-r from-amber-400 to-orange-500 px-4 py-2 text-sm font-semibold text-slate-900 shadow-lg hover:shadow-amber-500/30"
              >
                <PlusCircle size={18} />
                Nouveau client
              </button>
            </div>
          </div>
          {(error || success) && (
            <div
              className={`rounded-2xl px-4 py-3 text-sm font-medium ${
                error
                  ? "bg-rose-500/10 border border-rose-500/40 text-rose-200"
                  : "bg-emerald-500/10 border border-emerald-500/40 text-emerald-200"
              }`}
            >
              {error || success}
            </div>
          )}
        </header>

        <section className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {[
            {
              label: "Clients totaux",
              value: stats.totalCustomers,
              icon: <Users />,
              accent: "text-sky-400",
              chip: "+ base récurrente",
            },
            {
              label: "Nouveaux ce mois",
              value: stats.newCustomersThisMonth,
              icon: <UserPlus />,
              accent: "text-emerald-400",
              chip: "Sessions actuelles",
            },
            {
              label: "Clients fidèles",
              value: stats.loyalCustomers || 0,
              icon: <ShieldCheck />,
              accent: "text-amber-300",
              chip: `≥ ${LOYAL_THRESHOLD} commandes`,
            },
            {
              label: "Anniversaires du mois",
              value: stats.birthdaysThisMonth?.length || 0,
              icon: <Calendar />,
              accent: "text-pink-300",
              chip: "Fidélisation",
            },
          ].map((card) => (
            <div
              key={card.label}
              className={`${cardClasses} rounded-3xl p-5 flex flex-col gap-3`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-xs uppercase tracking-[0.3em] ${subtleText}`}>
                    {card.label}
                  </p>
                  <p className="text-3xl font-semibold mt-2">{card.value}</p>
                </div>
                <div className={`text-2xl ${card.accent}`}>{card.icon}</div>
              </div>
              <span className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">
                {card.chip}
              </span>
            </div>
          ))}
        </section>

        <div className="grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-6">
            <div className={`${cardClasses} rounded-3xl`}>
              <div className="p-6 border-b border-white/5 flex flex-col gap-4">
                <div className="flex items-center justify-between gap-4 flex-col md:flex-row">
                  <h2 className="text-xl font-semibold flex items-center gap-2">
                    <Filter size={18} />
                    Répertoire clients
                  </h2>
                  <div className="flex items-center gap-3">
                    <div className="flex gap-2">
                      {filterOptions.map((chip) => (
                        <button
                          key={chip.value}
                          onClick={() => setActiveFilter(chip.value)}
                          className={`px-3 py-1 text-xs font-semibold rounded-full border ${
                            activeFilter === chip.value
                              ? "bg-amber-400 text-slate-900 border-amber-400"
                              : "border-white/15 hover:border-amber-400/50"
                          }`}
                        >
                          {chip.label}
                        </button>
                      ))}
                    </div>
                    <button
                      onClick={() => fetchCustomers(searchTerm)}
                      className="rounded-full border border-white/10 p-2 hover:border-amber-400/60"
                      title="Actualiser"
                    >
                      <RefreshCw
                        size={18}
                        className={loading ? "animate-spin" : ""}
                      />
                    </button>
                  </div>
                </div>
                <div className="flex flex-col gap-3 md:flex-row">
                  <div className="relative flex-1">
                    <Search
                      size={18}
                      className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                    />
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Rechercher un nom, téléphone ou email..."
                      className={`w-full rounded-2xl bg-transparent border border-white/10 pl-11 pr-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400/50`}
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs ${subtleText}`}>Trier par</span>
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="rounded-2xl border border-white/10 bg-transparent px-3 py-2 text-sm focus:ring-2 focus:ring-amber-400/50"
                    >
                      {sortOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              <div className="max-h-[480px] overflow-y-auto divide-y divide-white/5">
                {loading ? (
                  <div className="flex flex-col items-center justify-center py-12 text-sm text-slate-400 gap-3">
                    <Loader2 className="animate-spin" />
                    Chargement du répertoire...
                  </div>
                ) : filteredCustomers.length === 0 ? (
                  <div className="py-10 text-center text-sm text-slate-400">
                    Aucun client correspondant pour le moment
                  </div>
                ) : (
                  filteredCustomers.map((customer) => {
                    const loyal = isCustomerLoyal(customer);
                    return (
                      <button
                        type="button"
                        key={customer.id}
                        onClick={() => handleSelectCustomer(customer)}
                        className={`w-full text-left px-6 py-4 transition ${
                          selectedCustomer?.id === customer.id
                            ? "bg-amber-400/10 border-l-4 border-amber-400"
                            : "hover:bg-white/5"
                        }`}
                      >
                        <div className="flex items-center justify-between gap-4">
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-2xl bg-white/5 flex items-center justify-center">
                              <Users size={22} className="text-amber-300" />
                            </div>
                            <div>
                              <p className="font-semibold text-lg">
                                {customer.civility} {customer.first_name}{" "}
                                {customer.last_name}
                              </p>
                              <div className={`text-xs ${subtleText} flex gap-4`}>
                                <span className="flex items-center gap-1">
                                  <Phone size={14} />
                                  {customer.phone}
                                </span>
                                {customer.email && (
                                  <span className="flex items-center gap-1">
                                    <Mail size={14} />
                                    {customer.email}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <p className="text-xs uppercase tracking-[0.3em] text-slate-400">
                                Commandes
                              </p>
                              <p className="font-semibold">
                                {customer.total_orders || 0}
                              </p>
                              <p className="text-xs">
                                {formatCurrency(customer.total_spent)}
                              </p>
                            </div>
                            <div className="flex flex-col gap-2 items-end min-w-[120px]">
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleFavoriteToggle(customer);
                                }}
                                className={`p-2 rounded-full border ${
                                  customer.is_favorite
                                    ? "border-amber-400 text-amber-300"
                                    : "border-white/10 hover:border-amber-400/60"
                                }`}
                                title="Basculer favori"
                              >
                                <Star
                                  size={18}
                                  className={
                                    customer.is_favorite
                                      ? "fill-amber-400 text-amber-400"
                                      : ""
                                  }
                                  fill={customer.is_favorite ? "currentColor" : "none"}
                                />
                              </button>
                              {isSameMonth(customer.birth_date) && (
                                <div className="flex gap-2">
                                  <span className="px-2 py-1 text-[11px] rounded-full bg-pink-500/15 text-pink-300 font-semibold">
                                    Anniversaire
                                  </span>
                                </div>
                              )}
                            </div>
                            <div className="flex gap-2">
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditCustomer(customer);
                                }}
                                className="rounded-full border border-white/10 p-2 hover:border-amber-400/60"
                              >
                                <Edit size={16} />
                              </button>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteCustomer(customer.id);
                                }}
                                className="rounded-full border border-white/10 p-2 hover:border-rose-400/60 text-rose-400"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </div>
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div className={`${cardClasses} rounded-3xl p-6`}>
              {selectedCustomer ? (
                <>
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <p className={`text-xs uppercase tracking-[0.3em] ${subtleText}`}>
                        Fiche client
                      </p>
                      <h3 className="text-2xl font-semibold">
                        {selectedCustomer.civility} {selectedCustomer.first_name}{" "}
                        {selectedCustomer.last_name}
                      </h3>
                    </div>
                    <button
                      onClick={() => handleFavoriteToggle(selectedCustomer)}
                      className={`p-2 rounded-full border ${
                        selectedCustomer.is_favorite
                          ? "border-amber-400 text-amber-300"
                          : "border-white/10"
                      }`}
                    >
                      <Star
                        size={18}
                        className={
                          selectedCustomer.is_favorite
                            ? "fill-amber-400 text-amber-400"
                            : ""
                        }
                        fill={selectedCustomer.is_favorite ? "currentColor" : "none"}
                      />
                    </button>
                  </div>
                  <div className="mt-4 space-y-3 text-sm">
                    <div className="flex items-center gap-2">
                      <Phone size={16} className="text-emerald-400" />
                      {selectedCustomer.phone}
                    </div>
                    {selectedCustomer.email && (
                      <div className="flex items-center gap-2">
                        <Mail size={16} className="text-sky-400" />
                        {selectedCustomer.email}
                      </div>
                    )}
                    {selectedCustomer.birth_date && (
                      <div className="flex items-center gap-2">
                        <Calendar size={16} className="text-pink-400" />
                        {formatDate(selectedCustomer.birth_date)}
                      </div>
                    )}
                  </div>
                  <div className="grid grid-cols-3 gap-3 mt-6">
                    {[
                      {
                        label: "Commandes",
                        value: selectedCustomer.total_orders || 0,
                      },
                      {
                        label: "Total dépensé",
                        value: formatNumber(selectedCustomer.total_spent),
                      },
                      {
                        label: "Panier moyen",
                        value: formatNumber(averageBasket(selectedCustomer)),
                      },
                    ].map((stat) => (
                      <div
                        key={stat.label}
                        className="rounded-2xl border border-white/10 p-3"
                      >
                        <p className={`text-[11px] uppercase tracking-[0.3em] ${subtleText}`}>
                          {stat.label}
                        </p>
                        <p className="text-lg font-semibold">{stat.value}</p>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-3 mt-4">
                    <button
                      onClick={() => handleEditCustomer(selectedCustomer)}
                      className="flex-1 rounded-2xl border border-white/10 py-2 text-sm font-semibold hover:border-amber-400/60"
                    >
                      Modifier
                    </button>
                    <button
                      onClick={() => handleDeleteCustomer(selectedCustomer.id)}
                      className="flex-1 rounded-2xl border border-rose-500/40 text-rose-200 py-2 text-sm font-semibold hover:border-rose-400"
                    >
                      Supprimer
                    </button>
                  </div>
                  <div className="mt-6">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold flex items-center gap-2">
                        <History size={16} />
                        Historique des commandes
                      </h4>
                    </div>
                    {ordersLoading ? (
                      <div className="flex items-center gap-2 text-sm text-slate-400">
                        <Loader2 className="animate-spin" size={16} />
                        Chargement des commandes...
                      </div>
                    ) : ordersError ? (
                      <p className="text-sm text-rose-300">{ordersError}</p>
                    ) : customerOrders.length === 0 ? (
                      <p className={`text-sm ${subtleText}`}>
                        Aucune commande récente pour ce client.
                      </p>
                    ) : (
                      <div className="space-y-3 max-h-64 overflow-y-auto pr-1">
                        {customerOrders.map((order) => (
                          <div
                            key={order.id}
                            className="rounded-2xl border border-white/10 px-3 py-2 flex items-center justify-between text-sm"
                          >
                            <div>
                              <p className="font-semibold">
                                {formatOrderLabel(
                                  order.display_number ||
                                    order.payment_number ||
                                    order.order_number,
                                )}
                              </p>
                              <p className={`text-xs ${subtleText}`}>
                                {formatDateTime(order.created_at)}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="font-semibold">
                                {formatCurrency(order.total_amount)}
                              </p>
                              <p
                                className={`text-xs ${
                                  order.payment_status === "paid"
                                    ? "text-emerald-400"
                                    : "text-amber-300"
                                }`}
                              >
                                {order.payment_status || order.status}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="flex flex-col items-center gap-3 text-center text-sm text-slate-400">
                  <User size={32} />
                  Sélectionnez un client pour afficher ses détails
                </div>
              )}
            </div>

            {stats.birthdaysThisMonth?.length > 0 && (
              <div className={`${cardClasses} rounded-3xl p-5`}>
                <h3 className="font-semibold flex items-center gap-2 mb-3">
                  <Gift size={18} className="text-pink-400" />
                  Anniversaires du mois
                </h3>
                <div className="space-y-2">
                  {stats.birthdaysThisMonth.map((customer) => (
                    <button
                      key={customer.id}
                      type="button"
                      onClick={() => handleSelectCustomer(customer)}
                      className="w-full flex items-center justify-between rounded-2xl border border-white/10 px-3 py-2 hover:border-amber-400/60"
                    >
                      <div>
                        <p className="font-semibold">
                          {customer.first_name} {customer.last_name}
                        </p>
                        <p className={`text-xs ${subtleText}`}>
                          {formatDate(customer.birth_date)}
                        </p>
                      </div>
                      <Calendar size={16} className="text-pink-300" />
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className={`${cardClasses} rounded-3xl p-5`}>
              <div className="flex flex-col gap-3 mb-4">
                <div className="flex items-center justify-between gap-3">
                  <h3 className="font-semibold flex items-center gap-2">
                    <TrendingUp size={18} className="text-emerald-400" />
                    Meilleurs clients
                  </h3>
                  <div className="flex gap-2">
                    {topPeriodOptions.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => setTopPeriod(option.value)}
                        className={`px-3 py-1 text-xs rounded-full border ${
                          topPeriod === option.value
                            ? "border-emerald-400 text-emerald-300"
                            : "border-white/10"
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <span className={subtleText}>Afficher</span>
                  <input
                    type="number"
                    min={1}
                    max={50}
                    value={topLimit}
                    onChange={(e) => handleTopLimitChange(e.target.value)}
                    className="w-20 rounded-full border border-white/15 bg-transparent px-3 py-1 focus:ring-2 focus:ring-emerald-400/40 text-sm"
                  />
                  <span className={subtleText}>clients</span>
                </div>
              </div>
              {topCustomers.length === 0 ? (
                <p className={`text-sm ${subtleText}`}>
                  Pas de données sur la période sélectionnée
                </p>
              ) : (
                <div className="space-y-3">
                  {topCustomers.map((customer, index) => (
                    <div
                      key={customer.id}
                      className="flex items-center justify-between rounded-2xl border border-white/10 px-3 py-2"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-emerald-500/20 text-emerald-200 flex items-center justify-center text-sm font-bold">
                          {index + 1}
                        </div>
                        <div>
                          <p className="font-semibold">
                            {customer.first_name} {customer.last_name}
                          </p>
                          <p className={`text-xs ${subtleText}`}>
                            {customer.orders_count} commandes
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold">
                          {formatCurrency(customer.total_amount)}
                        </p>
                        <p className={`text-xs ${subtleText}`}>
                          Panier {formatCurrency(customer.average_amount)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {stats.topCustomers?.length > 0 && (
              <div className={`${cardClasses} rounded-3xl p-5`}>
                <h3 className="font-semibold flex items-center gap-2 mb-3">
                  <Award size={18} className="text-indigo-400" />
                  Hall of fame (global)
                </h3>
                <div className="space-y-2">
                  {stats.topCustomers.slice(0, 3).map((customer, index) => (
                    <div
                      key={customer.id}
                      className="flex items-center justify-between rounded-2xl border border-white/10 px-3 py-2"
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-xs font-bold text-indigo-300">
                          #{index + 1}
                        </span>
                        <div>
                          <p className="font-semibold">
                            {customer.first_name} {customer.last_name}
                          </p>
                          <p className={`text-xs ${subtleText}`}>
                            {customer.total_orders} commandes
                          </p>
                        </div>
                      </div>
                      <span className="font-semibold">
                        {formatCurrency(customer.total_spent)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const CustomerFormModal = ({
  isOpen,
  onClose,
  onSubmit,
  formData,
  setFormData,
  isEditing,
  isSubmitting,
  isDarkMode,
}) => {
  if (!isOpen) return null;

  const textMuted = isDarkMode ? "text-slate-400" : "text-slate-500";
  const cardClasses = isDarkMode
    ? "bg-[#0f172a] border border-white/10 text-slate-100"
    : "bg-white border border-slate-200 text-slate-900";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur">
      <div className={`${cardClasses} w-full max-w-lg rounded-3xl p-6 shadow-2xl`}>
        <form onSubmit={onSubmit} className="space-y-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.4em] text-amber-400">
                {isEditing ? "Modifier" : "Ajouter"}
              </p>
              <h2 className="text-2xl font-semibold">Client</h2>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-full border border-white/10 px-3 py-1 text-sm hover:border-amber-400/60"
            >
              Fermer
            </button>
          </div>

          <CustomerProfileFields value={formData} onChange={setFormData} />

          <label className="flex items-center gap-3 text-sm font-semibold">
            <input
              type="checkbox"
              checked={formData.is_favorite}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  is_favorite: e.target.checked,
                }))
              }
              className="rounded accent-amber-500"
            />
            <span className={textMuted}>Marquer comme client favori</span>
          </label>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-white/15 px-4 py-2 text-sm font-semibold hover:border-amber-400/60"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="rounded-xl bg-gradient-to-r from-amber-400 to-orange-500 px-4 py-2 text-sm font-semibold text-slate-900 shadow-lg hover:shadow-amber-500/40 disabled:opacity-70"
            >
              {isSubmitting ? "Enregistrement..." : "Enregistrer"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CustomerManagement;
